import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { generateDefaultValues, schema, Schema } from "./schema";
import { ProjectData } from "../../../../models/ProjectData";
import DataSvc from "../../../../services/dataSvc";
import { HookForm } from "../../../HookForm/HookForm";
import { HookFormTextarea } from "../../../HookForm/Textarea";

interface IProjectCustomNotesFormProps {
  project: ProjectData;
  allowSubmit: boolean;
}

export const ProjectCustomNotesForm = (props: IProjectCustomNotesFormProps) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`projectManagementPage.projectCustomNotes.${key}`);

  const { project, allowSubmit } = props;

  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => {
    return generateDefaultValues(project);
  }, [project]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data: Schema) => {
    const payload = {
      custom_notes: data.customNotes,
    };
    try {
      await DataSvc.updateProject({
        organizationId: project.organization_id,
        projectId: project.id.toString(),
        payload,
      });

      toast.success(t("success"));
      // Invalidate queries so data is refreshed
      // (This is necessary because if the user navigates away and back to this page, the memo field will be old)
      queryClient.invalidateQueries({
        queryKey: ["projects", project.organization_id],
      });
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  return (
    <HookForm
      disableSubmit={!allowSubmit}
      showSubmitButton={allowSubmit}
      onSubmit={onSubmit}
      form={form}
      submitButtonLabel={t("update")}
    >
      <HookFormTextarea
        disabled={!allowSubmit}
        name="customNotes"
      />
    </HookForm>
  );
};
